/**
 * The AI assistant (Ctrl+J): a chat about your notes, beside the page. Claude
 * searches and reads pages through tools that run here; answers cite pages as
 * links, and the pages it read are listed under each answer.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Loader2, RotateCcw, Send, Sparkles, Square, X } from 'lucide-react';
import { usePages, useStore } from '../db/context';
import { cn } from '../lib/cn';
import { useUi } from '../state/ui';
import { formatKeys, SHORTCUT_KEYS } from '../shortcuts';
import { ActionCards } from './ActionCards';
import { AnswerText } from './AnswerText';
import { useAiChat, type ChatItem } from './chat';
import { AiNotReady } from './AiNotReady';
import { routeLabel, useAiReady } from './providers';

const SUGGESTIONS = ['today', 'summarize', 'find'] as const;

export function AiPanel() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const pages = usePages();
  const { set, open } = useUi();
  const readiness = useAiReady('chat');
  const ready = readiness.status === 'ready';
  const { items, running, send, stop, reset } = useAiChat();
  const [draft, setDraft] = useState('');
  const input = useRef<HTMLTextAreaElement>(null);
  const end = useRef<HTMLDivElement>(null);

  useEffect(() => {
    input.current?.focus();
  }, []);
  useEffect(() => {
    end.current?.scrollIntoView({ block: 'end' });
  }, [items]);

  const ask = (question: string) => {
    if (!question.trim() || running) return;
    setDraft('');
    void send(store, question, i18n.language);
  };
  const openTitle = async (title: string) => {
    const page = await store.findPageByName(title);
    if (page) open({ kind: 'page', pageId: page.id });
  };

  return (
    <aside
      aria-label={t('ai.title')}
      className="fixed inset-0 z-40 flex flex-col bg-bg pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] md:static md:my-2 md:mr-2 md:h-auto md:w-[22rem] md:shrink-0 md:overflow-hidden md:rounded-sheet md:p-0 md:shadow-sheet lg:w-[26rem]"
    >
      <header className="flex h-11 shrink-0 items-center gap-2 border-b border-border px-3">
        <Sparkles className="size-4 text-accent-ink" />
        <h2 className="flex-1 text-sm font-semibold">{t('ai.title')}</h2>
        <span className="truncate text-xs text-fg-muted">{routeLabel(readiness.route)}</span>
        <button
          type="button"
          onClick={reset}
          disabled={items.length === 0}
          aria-label={t('ai.newChat')}
          title={t('ai.newChat')}
          className="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-30"
        >
          <RotateCcw className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => set({ aiOpen: false })}
          aria-label={t('ai.close')}
          title={`${t('ai.close')} (${formatKeys(SHORTCUT_KEYS.ai)})`}
          className="rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg"
        >
          <X className="size-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 text-sm" aria-live="polite">
        {!ready ? (
          <AiNotReady status={readiness.status} />
        ) : items.length === 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-fg-muted">{t('ai.intro')}</p>
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => ask(t(`ai.suggestions.${s}`))}
                className="rounded-lg border border-border px-2 py-1.5 text-left hover:bg-bg-hover"
              >
                {t(`ai.suggestions.${s}`)}
              </button>
            ))}
            <p className="mt-2 text-xs text-fg-muted">{t('ai.privacy')}</p>
          </div>
        ) : (
          <ol className="flex flex-col gap-4">
            {items.map((item) => (
              <Message
                key={item.id}
                item={item}
                titleOf={(id) => pages?.byId.get(id)?.title}
                onLink={(title) => void openTitle(title)}
                onOpen={(id) => open({ kind: 'page', pageId: id })}
              />
            ))}
          </ol>
        )}
        <div ref={end} />
      </div>

      {ready && (
        <form
          className="flex shrink-0 items-end gap-2 border-t border-border p-2"
          onSubmit={(e) => {
            e.preventDefault();
            ask(draft);
          }}
        >
          <textarea
            ref={input}
            value={draft}
            rows={Math.min(6, Math.max(1, draft.split('\n').length))}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                ask(draft);
              }
            }}
            placeholder={t('ai.placeholder')}
            aria-label={t('ai.question')}
            className="max-h-40 min-h-9 flex-1 resize-none rounded-lg border border-border bg-bg px-2 py-1.5 text-sm outline-none focus:border-accent"
          />
          {running ? (
            <button
              type="button"
              onClick={stop}
              aria-label={t('ai.stop')}
              className="rounded-lg border border-border p-2 hover:bg-bg-hover"
            >
              <Square className="size-4" />
            </button>
          ) : (
            <button
              type="submit"
              disabled={!draft.trim()}
              aria-label={t('ai.send')}
              className="rounded-full bg-accent p-2 text-accent-fg disabled:opacity-40"
            >
              <Send className="size-4" />
            </button>
          )}
        </form>
      )}
    </aside>
  );
}

function Message({
  item,
  titleOf,
  onLink,
  onOpen,
}: {
  item: ChatItem;
  titleOf: (id: string) => string | undefined;
  onLink: (title: string) => void;
  onOpen: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (item.role === 'user')
    return (
      <li className="self-end rounded-lg bg-accent/10 px-3 py-2 whitespace-pre-wrap">
        {item.text}
      </li>
    );
  const sources = (item.sources ?? []).filter((id) => titleOf(id) !== undefined);
  return (
    <li className="flex flex-col gap-2" data-testid="ai-answer">
      {item.text && <AnswerText text={item.text} onLink={onLink} />}
      {item.pending && (
        <p className="flex items-center gap-2 text-xs text-fg-muted">
          <Loader2 className="size-3.5 animate-spin" />
          {item.tool ? t(`ai.tools.${item.tool}`, t('ai.working')) : t('ai.thinking')}
        </p>
      )}
      {item.actions && <ActionCards cards={item.actions} />}
      {item.error && (
        <p role="alert" className={cn('rounded-md bg-red-500/10 px-2 py-1.5 text-danger')}>
          {t(`ai.errors.${item.error}`)}
        </p>
      )}
      {sources.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 text-xs text-fg-muted">
          <BookOpen className="size-3.5" aria-hidden />
          <span>{t('ai.sources')}</span>
          {sources.map((id) => (
            <button
              key={id}
              type="button"
              onClick={() => onOpen(id)}
              className="rounded-md border border-border px-1.5 py-0.5 hover:bg-bg-hover hover:text-fg"
            >
              {titleOf(id) || t('page.untitled')}
            </button>
          ))}
        </div>
      )}
    </li>
  );
}
